import { getSupabaseClient } from './supabase';
import { fetchTransferAvailabilityBatch } from './transfers';
import { AppError, toAppError, toPartialSaveError, type AppErrorKind } from '../utils/appErrors';

/**
 * New transfer, Add to transfer and Scan to receive (docs/warehouse.md).
 *
 * `create_transfer` and `transfer_update_draft` (20261007171200_transfer_portal_actions) and
 * `search_transfer_items` / `find_receivable_by_code` (20261007171400_warehouse_library_rpcs) are
 * the Warehouse RPCs. Their JSON is parsed field by field; a missing or null field becomes null
 * rather than an error. Writes go only through these functions, so the From-site, R2 and
 * nothing-picked rules and the transfer's history apply to every path.
 */

type Json = { [key: string]: unknown };

export type TransferScope = 'temporary' | 'permanent';
export type TransferTracking = 'serial' | 'quantity';

/* ------------------------------------------------------------------ */
/*  Query keys                                                         */
/* ------------------------------------------------------------------ */

/** Everything here lives under `['warehouse', …]`, so a create or add refreshes it with the library. */
export const transferCreateQueryKeys = {
  root: ['warehouse'] as const,
  itemSearch: (params: TransferItemSearchParams) => [
    'warehouse',
    'transfer-item-search',
    params.fromLocationId,
    params.toLocationId ?? '',
    params.shipAt ?? '',
    params.returnAt ?? '',
    params.eventId ?? '',
    normalizeSearchText(params.query).toLowerCase(),
    params.limit ?? DEFAULT_ITEM_SEARCH_LIMIT,
  ] as const,
  lineAvailability: (fromLocationId: string, startAt: string, endAt: string, itemIds: readonly string[]) => [
    'warehouse',
    'transfer-line-availability',
    fromLocationId,
    startAt,
    endAt,
    [...itemIds].sort().join(','),
  ] as const,
  eventSearch: (term: string) => ['warehouse', 'transfer-event-search', normalizeSearchText(term).toLowerCase()] as const,
  event: (eventId: string) => ['warehouse', 'transfer-event', eventId] as const,
  vehicles: (locationId: string) => ['warehouse', 'transfer-vehicles', locationId] as const,
  addableTransfers: (search: string, siteKey: string) => [
    'warehouse',
    'addable-transfers',
    normalizeSearchText(search).toLowerCase(),
    siteKey,
  ] as const,
};

/* ------------------------------------------------------------------ */
/*  Defensive JSON helpers                                             */
/* ------------------------------------------------------------------ */

function asObject(value: unknown): Json | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Json : null;
}

function asText(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length > 0 ? text : null;
}

function firstText(...values: unknown[]): string | null {
  for (const value of values) {
    const text = asText(value);
    if (text) return text;
  }
  return null;
}

function asNullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

function asBoolean(value: unknown): boolean | null {
  if (value === true || value === 'true' || value === 't') return true;
  if (value === false || value === 'false' || value === 'f') return false;
  return null;
}

/** Rows from a set-returning RPC (an array) or from a jsonb object that holds the array. */
function rowsOf(data: unknown, keys: readonly string[]): Json[] {
  const object = asObject(data);
  const key = object ? keys.find((candidate) => Array.isArray(object[candidate])) : undefined;
  const list: unknown[] = Array.isArray(data) ? data : key && object ? object[key] as unknown[] : [];
  return list.map((row) => asObject(row)).filter((row): row is Json => row != null);
}

function positiveCeil(value: number | null): number | null {
  return value != null && value > 0 ? Math.ceil(value) : null;
}

function clampLimit(value: number | undefined, fallback: number, max: number): number {
  const number = Math.trunc(Number(value ?? fallback));
  return Number.isFinite(number) ? Math.max(1, Math.min(max, number)) : fallback;
}

function normalizeSearchText(value: string): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, 80);
}

/** Text that is safe inside a PostgREST `.or()` filter and an ILIKE pattern. */
function filterSafeText(value: string): string {
  return normalizeSearchText(String(value ?? '').replace(/[%_*\\(),"]/g, ' '));
}

function cleanText(value: string | null | undefined): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  return text ? text : null;
}

function uniqueIds(values: ReadonlyArray<string | null | undefined>): string[] {
  return Array.from(new Set(values.filter((value): value is string => typeof value === 'string' && value.length > 0)));
}

/* ------------------------------------------------------------------ */
/*  Errors from the Warehouse actions                                  */
/* ------------------------------------------------------------------ */

// create_transfer raises its rule as the message ('transfer_asset_not_at_source'); the shared
// helpers raise a sentence with the rule in HINT ('warehouse_transfer_site_required').
const TRANSFER_ACTION_TEXT: { [key: string]: string } = {
  transfer_payload_invalid: 'The transfer could not be read. Refresh the page and try again.',
  transfer_sites_invalid: 'Choose two different sites for From and To.',
  transfer_site_not_found: 'One of the sites is no longer available. Choose the sites again.',
  transfer_event_not_found: 'The event is no longer available. Remove it or choose another.',
  transfer_scope_invalid: 'Choose Temporary or Permanent.',
  transfer_lines_invalid: 'The items could not be read. Remove them and add them again.',
  transfer_too_many_lines: 'A transfer holds up to 200 lines. Split these items into two transfers.',
  transfer_line_invalid: 'One of the lines could not be read. Remove it and add it again.',
  transfer_line_reference_required: 'Every line needs an item, a vehicle or a name.',
  transfer_line_quantity_invalid: 'Quantities must be between 1 and 100,000.',
  transfer_line_item_not_found: 'An item is no longer in the catalog. Remove it and search again.',
  transfer_line_duplicate_assets: 'The same serial was chosen twice on one line.',
  transfer_line_more_assets_than_quantity: 'A line has more chosen serials than its quantity.',
  transfer_asset_not_found: 'A chosen serial no longer exists. Remove it and search again.',
  transfer_asset_wrong_item: 'A chosen serial belongs to a different item.',
  transfer_asset_not_at_source: 'A chosen serial is not at the From site.',
  transfer_asset_on_other_transfer: 'A chosen serial is already on another open transfer.',
  transfer_not_editable: 'This transfer can no longer be changed.',
  transfer_edit_requires_nothing_picked: 'Items on this transfer are already picked, so it can no longer be changed.',
  transfer_r2_managed: 'R2 manages this transfer, so change it in R2.',
  warehouse_request_key_reused: 'This was already sent with different details. Close the dialog, refresh and try again.',
  warehouse_request_in_progress: 'This transfer is still being created. Wait a moment, then check the Warehouse list.',
};

function kindForCode(code: string | null): AppErrorKind {
  if (code === '42501') return 'access_denied';
  if (code === '55000' || code === '40001') return 'conflict';
  if (code && (/^22/.test(code) || code === '23503' || code === '23514' || code === 'P0002')) return 'validation';
  return 'unexpected';
}

/** A readable error for a failed Warehouse action, without backend codes or ids. */
export function warehouseActionError(error: unknown, fallback: string, context: string): AppError {
  if (error instanceof AppError) return error;
  const candidate = asObject(error);
  const message = asText(candidate?.message) ?? '';
  const hint = asText(candidate?.hint) ?? '';
  const code = asText(candidate?.code);
  const mapped = TRANSFER_ACTION_TEXT[message] ?? TRANSFER_ACTION_TEXT[hint];
  if (mapped) return new AppError(kindForCode(code), mapped, { code: code ?? undefined, context, cause: error });
  // private.warehouse_fail: a sentence written for people, with the rule key in HINT.
  if (/^(?:warehouse|transfer)_[a-z0-9_]+$/.test(hint) && message && !/^[a-z0-9_]+$/.test(message)) {
    return new AppError(kindForCode(code), message, { code: code ?? undefined, context, cause: error });
  }
  return toAppError(error, fallback, context, 'mutation');
}

/* ------------------------------------------------------------------ */
/*  create_transfer                                                    */
/* ------------------------------------------------------------------ */

export type TransferCreateLine = {
  itemId?: string | null;
  productId?: string | null;
  vehicleId?: string | null;
  /** A line for something not in the catalog. */
  externalName?: string | null;
  externalCode?: string | null;
  accessory?: boolean;
  qty: number;
  /** Serials chosen now; the rest of the line is scanned when picked. */
  assetIds?: readonly string[];
  notes?: string | null;
  /** The event line this transfer line covers (event workspace prefill). */
  eventLineId?: string | null;
};

export type TransferCreateInput = {
  fromLocationId: string;
  toLocationId: string;
  scope: TransferScope;
  description?: string | null;
  needByAt?: string | null;
  expectedShipAt?: string | null;
  expectedReceiveAt?: string | null;
  /** Temporary transfers only; ignored for permanent ones. */
  expectedReturnAt?: string | null;
  eventId?: string | null;
  lines: readonly TransferCreateLine[];
};

export type TransferCreateResult = {
  transferId: string;
  docNo: string | null;
  /** The same request key had already created this transfer (a retried submit). */
  replayed: boolean;
};

/**
 * The `p_transfer` payload of `create_transfer` (contract names: `scope`, `external_name`,
 * `external_code`, `accessory`). Lines also carry what the old modal set: `is_permanent` on every
 * line and the return date on vehicle lines, plus the event line a prefilled line covers.
 */
export function buildCreateTransferPayload(input: TransferCreateInput): Json {
  const permanent = input.scope === 'permanent';
  const returnAt = permanent ? null : input.expectedReturnAt ?? null;
  return {
    from_location_id: input.fromLocationId,
    to_location_id: input.toLocationId,
    scope: input.scope,
    description: cleanText(input.description),
    need_by_at: input.needByAt ?? null,
    expected_ship_at: input.expectedShipAt ?? null,
    expected_receive_at: input.expectedReceiveAt ?? null,
    expected_return_at: returnAt,
    event_id: input.eventId ?? null,
    lines: input.lines.map((line) => {
      const externalName = line.itemId || line.productId || line.vehicleId ? null : cleanText(line.externalName);
      return {
        item_id: line.itemId ?? null,
        product_id: line.productId ?? null,
        vehicle_id: line.vehicleId ?? null,
        external_name: externalName,
        external_code: externalName ? cleanText(line.externalCode) : null,
        accessory: Boolean(externalName && line.accessory),
        qty: line.vehicleId ? 1 : line.qty,
        asset_ids: line.assetIds ? [...line.assetIds] : [],
        notes: cleanText(line.notes),
        is_permanent: permanent,
        expected_return_at: line.vehicleId ? returnAt : null,
        event_line_id: line.eventLineId ?? null,
        source_event_id: line.eventLineId && input.eventId ? input.eventId : null,
      };
    }),
  };
}

/** Creates a Portal transfer in one request. Reuse `requestKey` when retrying the same submit. */
export async function createTransfer(requestKey: string, input: TransferCreateInput): Promise<TransferCreateResult> {
  const { data, error } = await getSupabaseClient().rpc('create_transfer', {
    p_request_key: requestKey,
    p_transfer: buildCreateTransferPayload(input),
  });
  if (error) throw warehouseActionError(error, 'The transfer was not created.', 'transfers.create');
  const result = asObject(Array.isArray(data) ? data[0] : data) ?? {};
  const transferId = firstText(result.transfer_id, result.id);
  if (!transferId) {
    throw new AppError('unexpected', 'The transfer may not have been created. Check the Warehouse list before trying again.', {
      context: 'transfers.create',
    });
  }
  return { transferId, docNo: asText(result.doc_no), replayed: result.replayed === true };
}

/* ------------------------------------------------------------------ */
/*  search_transfer_items                                              */
/* ------------------------------------------------------------------ */

const DEFAULT_ITEM_SEARCH_LIMIT = 12;

export type TransferItemAsset = {
  id: string;
  serial: string | null;
  barcode: string | null;
  /** Allocatable: not held for repair, inspection or the like. (Every unit returned is at the From site.) */
  available: boolean;
  /** The unit's status when it can't be allocated ("In repair"). */
  note: string | null;
};

export type TransferItemSearchResult = {
  /** Stable option key: `item:<id>`, or `asset:<id>` for a unit that matched the typed code. */
  key: string;
  itemId: string;
  /** Always null today: search_transfer_items returns catalog items only. */
  productId: string | null;
  name: string;
  sku: string | null;
  /** Always null today: search_transfer_items does not return a category. */
  category: string | null;
  /** 'serial' when units are picked by scan; null when unknown (the catalog fallback). */
  tracking: TransferTracking | null;
  /** Free at the From site for the window; null when unknown. */
  freeAtFrom: number | null;
  /** Units on other open transfers shipping from the From site that week. */
  onOtherTransfers: number;
  /** The event's shortage, when the event's site is the To site. */
  suggestedQty: number | null;
  /** Set when the typed code matched one unit (barcode, serial, RFID or extra barcode). */
  asset: TransferItemAsset | null;
};

export type TransferItemSearchParams = {
  fromLocationId: string;
  /** Needed for the event's shortage: it is suggested only when the event's site is the To site. */
  toLocationId: string | null;
  query: string;
  shipAt: string | null;
  returnAt: string | null;
  eventId: string | null;
  limit?: number;
};

/** `serialized` is the pick mode (not bulk and has countable units); `is_bulk` alone can't tell. */
function parseTracking(row: Json): TransferTracking | null {
  const serialized = asBoolean(row.serialized);
  if (serialized != null) return serialized ? 'serial' : 'quantity';
  return asBoolean(row.is_bulk) ? 'quantity' : null;
}

function sentenceCase(value: string | null): string | null {
  const words = (value ?? '').split(/[_\s-]+/).filter(Boolean).join(' ').toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : null;
}

function parseAsset(value: unknown): TransferItemAsset | null {
  const row = asObject(value);
  const id = asText(row?.asset_id);
  if (!row || !id) return null;
  const available = asBoolean(row.allocatable) !== false;
  return {
    id,
    serial: asText(row.serial_number),
    barcode: firstText(row.code, row.barcode),
    available,
    note: available ? null : sentenceCase(asText(row.status)) ?? 'Not available',
  };
}

/**
 * One `search_transfer_items` row: `{item_id, item_code, name, is_bulk, serialized, free_at_from,
 * on_other_transfers, suggested_qty, assets: [{asset_id, code, serial_number, status, allocatable}]}`.
 * Units that matched the typed code come first as their own options, then the item.
 */
function parseItemRow(row: Json): TransferItemSearchResult[] {
  const itemId = asText(row.item_id);
  if (!itemId) return [];
  const sku = asText(row.item_code);
  const base: TransferItemSearchResult = {
    key: `item:${itemId}`,
    itemId,
    productId: null,
    name: asText(row.name) ?? sku ?? 'Unnamed item',
    sku,
    category: null,
    tracking: parseTracking(row),
    freeAtFrom: asNullableNumber(row.free_at_from),
    onOtherTransfers: Math.max(0, asNullableNumber(row.on_other_transfers) ?? 0),
    suggestedQty: positiveCeil(asNullableNumber(row.suggested_qty)),
    asset: null,
  };
  const assets = (Array.isArray(row.assets) ? row.assets : [])
    .map(parseAsset)
    .filter((asset): asset is TransferItemAsset => asset != null);
  return [
    ...assets.map((asset) => ({ ...base, key: `asset:${asset.id}`, tracking: 'serial' as const, asset })),
    base,
  ];
}

function parseItemSearch(data: unknown): TransferItemSearchResult[] {
  const seen = new Set<string>();
  const results: TransferItemSearchResult[] = [];
  rowsOf(data, ['rows']).forEach((row) => {
    parseItemRow(row).forEach((result) => {
      if (seen.has(result.key)) return;
      seen.add(result.key);
      results.push(result);
    });
  });
  return results;
}

/**
 * Items by name, SKU or a unit's code, with what is free at the From site for the window. The
 * window should match the lines' free counts (`fetchTransferLineAvailability`): both read the same
 * availability snapshot, so the same window gives the same numbers.
 */
export async function searchTransferItems(params: TransferItemSearchParams): Promise<TransferItemSearchResult[]> {
  const query = normalizeSearchText(params.query);
  if (query.length < 2 || !params.fromLocationId) return [];
  const limit = clampLimit(params.limit, DEFAULT_ITEM_SEARCH_LIMIT, 50);
  const { data, error } = await getSupabaseClient().rpc('search_transfer_items', {
    p_from: params.fromLocationId,
    p_query: query,
    p_ship_at: params.shipAt,
    p_return_at: params.returnAt,
    p_event_id: params.eventId,
    p_limit: limit,
    p_to: params.toLocationId,
  });
  if (error) throw warehouseActionError(error, 'Item search is not available right now.', 'transfers.item-search');
  return parseItemSearch(data);
}

/** Free units per item at the From site for the window (one batch availability call). */
export async function fetchTransferLineAvailability(params: {
  fromLocationId: string;
  startAt: string;
  endAt: string;
  itemIds: readonly string[];
}): Promise<{ [itemId: string]: number }> {
  const itemIds = uniqueIds(params.itemIds).slice(0, 200);
  if (!params.fromLocationId || itemIds.length === 0) return {};
  const candidates = itemIds.map((itemId) => ({
    item_id: itemId,
    location_id: params.fromLocationId,
    start_at: params.startAt,
    end_at: params.endAt,
    current_qty: 0,
    exclude_order_id: null,
    exclude_event_id: null,
    candidate_id: itemId,
  }));
  const { data, error } = await fetchTransferAvailabilityBatch(candidates, params.fromLocationId);
  if (error) throw toAppError(error, 'Free counts could not be loaded.', 'transfers.line-availability', 'query');
  const free: { [itemId: string]: number } = {};
  data.forEach((value: unknown) => {
    const row = asObject(value);
    const itemId = asText(row?.candidate_id);
    const available = asNullableNumber(row?.available_qty);
    if (itemId && available != null) free[itemId] = Math.max(0, available);
  });
  return free;
}

/* ------------------------------------------------------------------ */
/*  Events, vehicles                                                   */
/* ------------------------------------------------------------------ */

export type TransferEventOption = {
  id: string;
  code: string | null;
  name: string | null;
  startDate: string | null;
  siteLocationId: string | null;
};

const EVENT_OPTION_SELECT = 'id,event_code,name,event_start_date,site_location_id';

function parseEventOption(value: unknown): TransferEventOption | null {
  const row = asObject(value);
  const id = asText(row?.id);
  if (!row || !id) return null;
  return {
    id,
    code: asText(row.event_code),
    name: asText(row.name),
    startDate: asText(row.event_start_date),
    siteLocationId: asText(row.site_location_id),
  };
}

/**
 * `[from, to)` for an event-code prefix on the `event_code` btree. Each query stays an index range
 * scan; the database collation is en_US, so only letters and digits are bumped.
 */
function eventCodePrefixRange(term: string): { from: string; to: string | null } | null {
  const code = term.trim().toUpperCase();
  if (!/^[0-9A-Z]{2,24}$/.test(code)) return null;
  const chars = code.split('');
  while (chars.length > 0) {
    const last = chars[chars.length - 1];
    if (last === '9' || last === 'Z') {
      chars.pop();
      continue;
    }
    chars[chars.length - 1] = String.fromCharCode(last.charCodeAt(0) + 1);
    return { from: code, to: chars.join('') };
  }
  return { from: code, to: null };
}

/**
 * Events by number prefix or name. Two bounded queries: an index range on `event_code` and the
 * trigram index on `name` (3+ characters). One OR over both columns scans every event (~1.2 s).
 */
export async function searchTransferEvents(params: { term: string; limit?: number }): Promise<TransferEventOption[]> {
  const term = filterSafeText(params.term);
  if (term.length < 2) return [];
  const limit = clampLimit(params.limit, 8, 20);
  const supabase = getSupabaseClient();
  const range = eventCodePrefixRange(term);
  let codeQuery = range
    ? supabase.from('events').select(EVENT_OPTION_SELECT).is('deleted_at', null).gte('event_code', range.from)
    : null;
  if (codeQuery && range?.to) codeQuery = codeQuery.lt('event_code', range.to);
  // Newest numbers first: codes are issued in sequence.
  const codeRequest = codeQuery ? codeQuery.order('event_code', { ascending: false }).limit(limit) : null;
  const nameQuery = term.length >= 3
    ? supabase
      .from('events')
      .select(EVENT_OPTION_SELECT)
      .is('deleted_at', null)
      .ilike('name', `%${term.replace(/\s+/g, '%')}%`)
      .order('event_start_date', { ascending: false, nullsFirst: false })
      .limit(limit)
    : null;
  const [codeResult, nameResult] = await Promise.all([codeRequest, nameQuery]);
  const error = codeResult?.error ?? nameResult?.error ?? null;
  if (error) throw toAppError(error, 'Event search is not available right now.', 'transfers.event-search', 'query');
  const seen = new Set<string>();
  const options: TransferEventOption[] = [];
  [...(codeResult?.data ?? []), ...(nameResult?.data ?? [])].forEach((value: unknown) => {
    const option = parseEventOption(value);
    if (!option || seen.has(option.id)) return;
    seen.add(option.id);
    options.push(option);
  });
  return options.slice(0, limit);
}

export async function fetchTransferEventOption(eventId: string): Promise<TransferEventOption | null> {
  if (!eventId) return null;
  const { data, error } = await getSupabaseClient()
    .from('events')
    .select(EVENT_OPTION_SELECT)
    .eq('id', eventId)
    .maybeSingle();
  if (error) throw toAppError(error, 'The event could not be loaded.', 'transfers.event', 'query');
  return parseEventOption(data);
}

export type TransferVehicleOption = {
  id: string;
  name: string;
  type: string | null;
  plate: string | null;
  locationId: string;
};

/** Active fleet vehicles currently at a site. */
export async function fetchTransferVehicles(locationId: string): Promise<TransferVehicleOption[]> {
  if (!locationId) return [];
  const { data, error } = await getSupabaseClient()
    .from('location_fleet_vehicles')
    .select('vehicle_id,name,vehicle_type,license_plate')
    .eq('current_location_id', locationId)
    .eq('is_active', true)
    .order('name', { ascending: true })
    .limit(200);
  if (error) throw toAppError(error, 'Vehicles could not be loaded.', 'transfers.vehicles', 'query');
  return (Array.isArray(data) ? data : [])
    .map((value: unknown) => {
      const row = asObject(value);
      const id = asText(row?.vehicle_id);
      if (!row || !id) return null;
      return {
        id,
        name: asText(row.name) ?? 'Vehicle',
        type: asText(row.vehicle_type),
        plate: asText(row.license_plate),
        locationId,
      };
    })
    .filter((vehicle): vehicle is TransferVehicleOption => vehicle != null);
}

/* ------------------------------------------------------------------ */
/*  Add to transfer                                                    */
/* ------------------------------------------------------------------ */

export type AddableTransfer = {
  id: string;
  docNo: string | null;
  status: string;
  scope: TransferScope;
  description: string | null;
  fromSite: { id: string; name: string | null } | null;
  toSite: { id: string; name: string | null } | null;
  needByAt: string | null;
  shipAt: string | null;
};

const ADDABLE_TRANSFER_SELECT = [
  'transfer_id',
  'doc_no',
  'status',
  'transfer_scope',
  'description',
  'need_by_at',
  'expected_ship_at',
  'from_location_id',
  'to_location_id',
  'from:locations!fk_to_from(location_id,name)',
  'to:locations!fk_to_to(location_id,name)',
].join(',');

function parseSite(value: unknown, fallbackId: unknown): { id: string; name: string | null } | null {
  const row = asObject(value);
  const id = firstText(row?.location_id, fallbackId);
  return id ? { id, name: asText(row?.name) } : null;
}

/**
 * Draft or confirmed Portal transfers (no R2 transfer IID, R2 transfer id or R2 contract IID, whose
 * lines the nightly sync rewrites), newest first, at most 50. `fromLocationIds` null = every site
 * (admins); an empty list matches nothing. Uses the status index (about 3.3k rows) before filtering.
 */
export async function searchAddableTransfers(params: {
  search: string;
  fromLocationIds: readonly string[] | null;
  limit?: number;
}): Promise<AddableTransfer[]> {
  if (params.fromLocationIds && params.fromLocationIds.length === 0) return [];
  let query = getSupabaseClient()
    .from('transfer_orders')
    .select(ADDABLE_TRANSFER_SELECT)
    .in('status', ['draft', 'confirmed'])
    .is('r2_transfer_order_iid', null)
    .is('r2_transfer_order_id', null)
    .is('r2_contract_iid', null);
  if (params.fromLocationIds) query = query.in('from_location_id', [...params.fromLocationIds]);
  const term = filterSafeText(params.search);
  if (term) {
    const pattern = `%${term.replace(/\s+/g, '%')}%`;
    query = query.or(`doc_no.ilike.${pattern},description.ilike.${pattern}`);
  }
  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(clampLimit(params.limit, 50, 50));
  if (error) throw toAppError(error, 'Open transfers could not be loaded.', 'transfers.addable', 'query');
  return (Array.isArray(data) ? data : [])
    .map((value: unknown) => {
      const row = asObject(value);
      const id = asText(row?.transfer_id);
      if (!row || !id) return null;
      return {
        id,
        docNo: asText(row.doc_no),
        status: asText(row.status) ?? 'draft',
        scope: row.transfer_scope === 'permanent' ? 'permanent' : 'temporary',
        description: asText(row.description),
        fromSite: parseSite(row.from, row.from_location_id),
        toSite: parseSite(row.to, row.to_location_id),
        needByAt: asText(row.need_by_at),
        shipAt: asText(row.expected_ship_at),
      };
    })
    .filter((transfer): transfer is AddableTransfer => transfer != null);
}

export type AddToTransferLine = {
  itemId?: string | null;
  productId?: string | null;
  /** A line for something not in the catalog. */
  externalName?: string | null;
  externalCode?: string | null;
  accessory?: boolean;
  qty: number;
};

/**
 * Adds lines to a draft or confirmed Portal transfer through `transfer_update_draft` (the record
 * page's rules: From-site staff, not R2, nothing picked yet; recorded in its history), then links
 * the event through `transfer_set_event_link` (an existing link is fine). Pass the same keys when
 * retrying the same add, so a lost answer never adds the lines twice.
 */
export async function addLinesToTransfer(params: {
  transfer: Pick<AddableTransfer, 'id' | 'scope'>;
  lines: readonly AddToTransferLine[];
  eventId?: string | null;
  requestKey: string;
  linkRequestKey: string;
}): Promise<void> {
  const permanent = params.transfer.scope === 'permanent';
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc('transfer_update_draft', {
    p_transfer_id: params.transfer.id,
    p_request_key: params.requestKey,
    p_patch: {
      lines: {
        add: params.lines.map((line) => {
          const externalName = line.itemId || line.productId ? null : cleanText(line.externalName);
          return {
            item_id: line.itemId ?? null,
            product_id: line.productId ?? null,
            external_name: externalName,
            external_code: externalName ? cleanText(line.externalCode) : null,
            accessory: Boolean(externalName && line.accessory),
            qty: line.qty,
            is_permanent: permanent,
            source_event_id: params.eventId ?? null,
          };
        }),
      },
    },
  });
  if (error) throw warehouseActionError(error, 'The items were not added to the transfer.', 'transfers.add-lines');
  if (!params.eventId) return;
  const { error: linkError } = await supabase.rpc('transfer_set_event_link', {
    p_transfer_id: params.transfer.id,
    p_request_key: params.linkRequestKey,
    p_event_id: params.eventId,
    p_linked: true,
  });
  if (linkError) {
    throw toPartialSaveError(
      linkError,
      'The items were added, but the transfer was not linked to the event. Link it from the transfer’s Events tab.',
      'transfers.add-lines',
    );
  }
}

/* ------------------------------------------------------------------ */
/*  find_receivable_by_code                                            */
/* ------------------------------------------------------------------ */

export type ReceivableKind = 'transfer' | 'subrental' | 'purchase_order';
export type ReceivableRefusal = 'r2_managed' | 'not_your_site' | 'nothing_outstanding';

export type ReceivableMatch = {
  key: string;
  kind: ReceivableKind;
  recordId: string;
  docNo: string | null;
  lineId: string | null;
  assetId: string | null;
  unitId: string | null;
  itemName: string | null;
  itemSku: string | null;
  receivable: boolean;
  /** Why it cannot be received here; null when receivable (or the server gave no reason). */
  reason: ReceivableRefusal | null;
};

function parseReceivableKind(value: unknown): ReceivableKind | null {
  const raw = asText(value)?.toLowerCase() ?? '';
  if (raw === 'transfer' || raw === 'transfer_order') return 'transfer';
  if (raw === 'purchase_order' || raw === 'po') return 'purchase_order';
  if (raw === 'subrental' || raw === 'xrental' || raw === 'xrental_order' || raw === 'cross_rental' || raw === 'vendor_rental') {
    return 'subrental';
  }
  return null;
}

function parseRefusal(value: unknown): ReceivableRefusal | null {
  const raw = asText(value)?.toLowerCase() ?? '';
  if (raw === 'r2_managed' || raw === 'not_your_site' || raw === 'nothing_outstanding') return raw;
  return null;
}

/** Records with something to receive for a scanned serial, barcode or document number (≤ 20). */
export async function findReceivableByCode(code: string, locationIds: readonly string[] | null): Promise<ReceivableMatch[]> {
  const trimmed = String(code ?? '').trim().slice(0, 200);
  if (!trimmed) return [];
  const { data, error } = await getSupabaseClient().rpc('find_receivable_by_code', {
    p_code: trimmed,
    p_location_ids: locationIds && locationIds.length > 0 ? [...locationIds] : null,
  });
  if (error) throw warehouseActionError(error, 'The scan could not be looked up.', 'transfers.find-receivable');
  const matches: ReceivableMatch[] = [];
  const seen = new Set<string>();
  rowsOf(data, ['rows', 'matches', 'results']).forEach((row) => {
    const kind = parseReceivableKind(row.kind);
    const recordId = firstText(row.record_id, row.id);
    if (!kind || !recordId) return;
    const item = asObject(row.item);
    const lineId = firstText(row.line_id);
    const assetId = firstText(row.asset_id);
    const unitId = firstText(row.unit_id);
    const key = [kind, recordId, lineId ?? '', assetId ?? unitId ?? ''].join(':');
    if (seen.has(key)) return;
    seen.add(key);
    const reason = parseRefusal(row.reason);
    matches.push({
      key,
      kind,
      recordId,
      docNo: firstText(row.doc_no),
      lineId,
      assetId,
      unitId,
      itemName: item ? firstText(item.name, item.item_name, item.name_internal) : firstText(row.item, row.item_name),
      itemSku: item ? firstText(item.sku, item.item_code, item.code) : firstText(row.item_code, row.sku),
      receivable: asBoolean(row.receivable) ?? !reason,
      reason,
    });
  });
  return matches;
}
